import { isNotDefined } from '@sgme/fp';
import { essentialFormApiConnector } from 'apiConnector/form/essential';
import { tabRemoved } from 'epic/workspace/common/tabs/tabsEpics';
import type { EnrichFormRequest } from 'models/forms/EnrichFormRequest';
import { ofType } from 'redux-observable';
import type { SchedulerLike } from 'rxjs';
import { EMPTY, of, throwError } from 'rxjs';
import { catchError, filter, map, mergeMap, takeUntil, timeout, withLatestFrom } from 'rxjs/operators';
import { selectEssentialFormInfos } from 'state/essential/infos/selectors';
import { essentialFormEnrichmentReceivedThunk } from 'state/essential/thunks';
import { getFormEnrichedData, getFormPendingEnrichmentData } from 'state/formData/formData.selectors';
import { commonFormActions } from 'state/forms/actions/form';
import type { EpicWithThunk } from 'state/state.common';
import { getWorkspaceType } from 'state/workspaces/tabs/tabs.selector';

export function essentialFormEnrichmentRequestedEpic(
  timeoutInMillis: number = 60000,
  appScheduler?: SchedulerLike,
): EpicWithThunk {
  return (action$, state$, { httpClient, frontLogger }) =>
    action$.pipe(
      ofType('FORM_ENRICHMENT_REQUESTED'),
      withLatestFrom(state$),
      filter(([{ tabId }, state]) => getWorkspaceType(state, tabId) === 'essential'),
      mergeMap(([{ tabId }, state]) => {
        const formInfos = selectEssentialFormInfos(state, tabId);
        if (isNotDefined(formInfos)) {
          return EMPTY;
        }
        const { negotiationId, version, sendWholeFormData } = formInfos;
        const enrichedData = getFormEnrichedData(state, tabId);
        const pendingData = getFormPendingEnrichmentData(state, tabId);

        const request: EnrichFormRequest = {
          tabId,
          negotiationId,
          version,
          productType: (pendingData.ProductType || enrichedData.ProductType) as string,
          productSubType: (pendingData.ProductSubtype || enrichedData.ProductSubtype) as string,
          wrapper: (pendingData.Wrapper || enrichedData.Wrapper) as string,
          variationParameters: pendingData,
          rfq: sendWholeFormData ? enrichedData : undefined,
        };

        const tabRemoved$ = tabRemoved(action$, tabId, state$);

        return essentialFormApiConnector(httpClient)
          .enrichForm(request)
          .pipe(
            takeUntil(tabRemoved$),
            map(
              ({
                values,
                blocksMetadata,
                fieldsMetadata,
                version: newVersion,
                actions,
                quoteQuery,
                errors,
                productView: newProductView,
                mustForceAdvancedMode,
                allowedRequests,
                productLayout,
              }) =>
                essentialFormEnrichmentReceivedThunk(
                  tabId,
                  newProductView,
                  blocksMetadata,
                  fieldsMetadata,
                  values,
                  newVersion,
                  actions,
                  quoteQuery,
                  mustForceAdvancedMode,
                  allowedRequests,
                  errors,
                  productLayout,
                ),
            ),
            timeout({
              each: timeoutInMillis,
              with: () => throwError(() => new Error('timeout')),
              scheduler: appScheduler,
            }),
            catchError(error => {
              frontLogger.error(
                'Enrichment failure detected on essential screen. Session linked to negotiation id {negotiationId} will be rollbacked {@FrontError}',
                negotiationId,
                error,
              );
              return of(commonFormActions.fetchFormEnrichmentFailed('essential', tabId, error));
            }),
          );
      }),
    );
}
